import React, { useState } from "react";

import { faTriangleExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";
import { PageBanner, Spinner, Text } from "@performyard/py-components";
import type { VideoConferencingConnection, VideoConferencingProviderEnum } from "@performyard/service-contracts";

import { VIDEO_CONFERENCING_PROVIDER_CONFIG } from "../../../../../components/VideoConferencing";
import { DisconnectionModal } from "./DisconnectionModal";
import { ProviderConnectionCard } from "./ProviderConnectionCard";

import {
	getVideoConferencingProviderListText,
	useDisconnectVideoConferencingIntegration,
	useMeetingsIntegrationsAvailableProviders,
	useUserVideoConferencingStatus,
	useVideoConferencingOAuthUrl,
} from "../../../../../api/hooks/defs/meetings-integrations";

type DisconnectTarget = { provider: VideoConferencingProviderEnum; displayName: string } | null;

type VideoConferencingProviderCardProps = {
	provider: VideoConferencingProviderEnum;
	displayName: string;
	connection: VideoConferencingConnection | null;
	isDisconnecting: boolean;
	onDisconnectRequest: (provider: VideoConferencingProviderEnum, displayName: string) => void;
	onCheckAgain: () => void;
	isCheckingAgain: boolean;
};

/**
 * One provider's card. Owns that provider's OAuth-initiate hook so the section can render a card per
 * server-listed provider without branching on provider identity (hooks can't be called in a loop).
 */
const VideoConferencingProviderCard = ({
	provider,
	displayName,
	connection,
	isDisconnecting,
	onDisconnectRequest,
	onCheckAgain,
	isCheckingAgain,
}: VideoConferencingProviderCardProps): React.ReactElement => {
	const { initiateVideoConferencingAuth, isLoading: isAuthLoading } = useVideoConferencingOAuthUrl({ provider });

	const { Logo, getTranscriptsUnavailableMessage } = VIDEO_CONFERENCING_PROVIDER_CONFIG[provider];
	const transcriptsUnavailableSubTitle = (connection && getTranscriptsUnavailableMessage?.(connection)) || "";

	return (
		<ProviderConnectionCard
			provider={provider}
			displayName={displayName}
			icon={<Logo size={40} />}
			isConnected={!!connection}
			connectedAs={connection?.accountEmail ?? null}
			isDisabled={isAuthLoading || isDisconnecting}
			onConnect={() => initiateVideoConferencingAuth()}
			onDisconnect={() => onDisconnectRequest(provider, displayName)}
			footer={
				transcriptsUnavailableSubTitle ? (
					<PageBanner
						color="primary"
						size="small"
						icon={faTriangleExclamation}
						mainTitle="Transcripts Unavailable"
						subTitle={transcriptsUnavailableSubTitle}
						rightActionButton={{
							children: "Check Again",
							onClick: () => onCheckAgain(),
							disabled: isCheckingAgain,
						}}
						data-testid={`${provider}-transcripts-unavailable-banner`}
					/>
				) : undefined
			}
		/>
	);
};

/** Video Conferencing section — renders one card per provider from the server registry. */
export const VideoConferencingSection = (): React.ReactElement => {
	const { mutateAsync: disconnectIntegration, isPending: isDisconnecting } =
		useDisconnectVideoConferencingIntegration();

	const { data: providersData, isLoading: isProvidersLoading } = useMeetingsIntegrationsAvailableProviders();
	const {
		data: statusData,
		isLoading: isStatusLoading,
		isFetching: isStatusFetching,
		refetch: refetchStatus,
	} = useUserVideoConferencingStatus({ includeUserSettings: true });

	const [disconnectTarget, setDisconnectTarget] = useState<DisconnectTarget>(null);

	const connections = statusData?.connections ?? [];

	const handleDisconnectRequest = (provider: VideoConferencingProviderEnum, displayName: string) => {
		setDisconnectTarget({ provider, displayName });
	};

	const handleConfirmDisconnect = async () => {
		if (!disconnectTarget) return;
		await disconnectIntegration({ provider: disconnectTarget.provider });
		setDisconnectTarget(null);
	};

	const isInitialLoading = isProvidersLoading || isStatusLoading;

	return (
		<div className="flex w-full flex-col gap-4">
			<div className="flex w-full flex-col gap-1">
				<Text size="paragraph-2" weight="semi-bold">
					Video Conferencing &amp; Transcripts
				</Text>
				<Text size="paragraph-3" weight="regular">
					Connect {getVideoConferencingProviderListText()} to automatically generate meeting links and capture
					transcripts for coaching insights and action items.
				</Text>
			</div>

			{isInitialLoading ? (
				<div className="flex h-[120px] w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{providersData?.providers.map(({ provider, displayName }) => (
						<VideoConferencingProviderCard
							key={provider}
							provider={provider}
							displayName={displayName}
							connection={connections.find((c) => c.provider === provider) ?? null}
							isDisconnecting={isDisconnecting}
							onDisconnectRequest={handleDisconnectRequest}
							onCheckAgain={refetchStatus}
							isCheckingAgain={isStatusFetching}
						/>
					))}
				</div>
			)}

			<DisconnectionModal
				isOpen={disconnectTarget !== null}
				title={`Disconnect ${disconnectTarget?.displayName ?? ""}?`}
				bodyText={`New meetings won't include a ${disconnectTarget?.displayName ?? ""} link and we'll no longer be able to fetch meeting transcripts. Existing meetings won't be affected.`}
				isDisconnecting={isDisconnecting}
				handleConfirmDisconnect={handleConfirmDisconnect}
				handleCancelDisconnect={() => setDisconnectTarget(null)}
			/>
		</div>
	);
};
